import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import Button from '../../common/Button';
import DiagnosisDropdown from '../../common/DiagnosisDropdown';
import PatientService from '../../../services/patientService';
import DentalNoteService from '../../../services/dentalNoteService';
import DiagnosisService from '../../../services/diagnosisService';
import PatientSectionHeader from '../common/PatientSectionHeader';
import Odontogram from './Odontogram';
import { getSurfaceName, SURFACE_KEYS } from '../../../utils/dentalSurfaceUtils';
import './NewDentalNote.css'; // Reutilizamos los mismos estilos
import './DiagnosisStyles.css';

const SURFACES = SURFACE_KEYS;

const EditDentalNote = () => {
  const { patientId, noteId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();

  const [mode, setMode] = useState('adult'); // 'adult' | 'pediatric'
  const [selected, setSelected] = useState({}); // { toothLabel: { surface: colorId } } - selección actual
  const [selectedDiagnosis, setSelectedDiagnosis] = useState(null); // Diagnóstico seleccionado actualmente
  const [currentNoteText, setCurrentNoteText] = useState(''); // Nota para la selección actual
  const [generalNotes, setGeneralNotes] = useState(''); // Notas generales de la nota dental
  const [addedSelections, setAddedSelections] = useState([]); // Array de selecciones agregadas
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [originalNote, setOriginalNote] = useState(null);
  const [isMultiDiagnosis, setIsMultiDiagnosis] = useState(false); // Para determinar qué tipo de nota es

  useEffect(() => {
    const loadPatient = async () => {
      if (patientId && auth.user?.access_token) {
        try {
          const patientData = await PatientService.getPatientById(patientId, auth.user.access_token);
          setPatient(patientData);
        } catch (error) {
          console.error('Error loading patient:', error);
        }
      }
    };
    loadPatient();
  }, [patientId, auth.user?.access_token]);

  useEffect(() => {
    const loadDentalNote = async () => {
      if (noteId && auth.user?.access_token) {
        try {
          setLoading(true);
          const response = await DentalNoteService.getDentalNoteById(noteId, auth.user.access_token);
          
          if (response.success && response.data) {
            const note = response.data;
            setOriginalNote(note);
            setMode(note.mode || 'adult');
            
            // Cargar notas generales
            setGeneralNotes(note.text || '');
            
            // Determinar si es una nota multi-diagnóstico o tradicional
            const hasSubNotes = note.subNotes && note.subNotes.length > 0;
            setIsMultiDiagnosis(hasSubNotes);
            
            if (hasSubNotes) {
              // Nota con múltiples diagnósticos - cargar todos los diagnósticos en paralelo
              try {
                const diagnosesById = await DiagnosisService.getDiagnosesMap(auth.user.access_token)
                  .catch(error => {
                    console.error('Error loading diagnoses:', error);
                    return new Map();
                  });
                
                const loadedSelections = note.subNotes.map((subNote, index) => {
                  const diagnosis = diagnosesById.get(subNote.diagnosisId) || {
                    id: subNote.diagnosisId,
                    name: 'Diagnóstico no encontrado',
                    color: '#gray',
                    isCompleteTooth: false
                  };
                  
                  // Convertir selecciones al formato del odontograma
                  const convertedSelections = {};
                  if (subNote.selections && Array.isArray(subNote.selections)) {
                    subNote.selections.forEach(selection => {
                      const toothLabel = selection.tooth;
                      const surface = denormalizeSurface(selection.surface);
                      const colorId = selection.colorId || selection.color || diagnosis.color;
                      
                      if (!convertedSelections[toothLabel]) {
                        convertedSelections[toothLabel] = {};
                      }
                      convertedSelections[toothLabel][surface] = colorId;
                    });
                  }
                  
                  return {
                    id: Date.now() + Math.random() + index, // ID temporal único para React
                    diagnosis: diagnosis,
                    selections: convertedSelections,
                    noteText: subNote.text || ''
                  };
                });
                
                setAddedSelections(loadedSelections);
              } catch (error) {
                console.error('Error processing multi-diagnosis note:', note.id, error);
                setAddedSelections([]);
              }
              
              setSelected({}); // No hay selección actual al cargar
              setCurrentNoteText('');
              setSelectedDiagnosis(null);
              
            } else {
              // Nota tradicional
              const convertedSelections = {};
              if (note.selections && Array.isArray(note.selections)) {
                note.selections.forEach(selection => {
                  const toothLabel = selection.tooth;
                  const surface = denormalizeSurface(selection.surface);
                  const colorId = selection.colorId || selection.color || '#gray';
                  
                  if (!convertedSelections[toothLabel]) {
                    convertedSelections[toothLabel] = {};
                  }
                  convertedSelections[toothLabel][surface] = colorId;
                });
              }
              setSelected(convertedSelections);
              setCurrentNoteText(note.text || '');

              // Cargar información del diagnóstico si existe
              if (note.diagnosisId) {
                try {
                  const diagnosis = await DiagnosisService.getDiagnosisById(note.diagnosisId, auth.user.access_token);
                  setSelectedDiagnosis(diagnosis);
                } catch (error) {
                  console.error('Error loading diagnosis:', error);
                  setSelectedDiagnosis({
                    id: note.diagnosisId,
                    name: 'Diagnóstico no encontrado',
                    color: '#gray',
                    isCompleteTooth: false
                  });
                }
              }
            }
          } else {
            console.error('Error loading dental note:', response.message);
            navigate(`/patients/${patientId}/dental-notes/legacy`);
          }
        } catch (error) {
          console.error('Error loading dental note:', error);
          navigate(`/patients/${patientId}/dental-notes/legacy`);
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadDentalNote();
  }, [noteId, auth.user?.access_token, patientId, navigate]);

  const goBack = () => {
    navigate(`/patients/${patientId}/dental-notes/legacy`);
  };

  const handleCurrentNoteTextChange = (value) => {
    setCurrentNoteText(value);
  };

  // Limpiar selección actual cuando cambie el diagnóstico (solo para editar notas multi-diagnóstico)
  const handleDiagnosisChange = (diagnosis) => {
    setSelectedDiagnosis(diagnosis);
    setSelected({}); // Limpiar odontograma
    setCurrentNoteText(''); // Limpiar nota actual
  };

  // Agregar selección actual a la lista (solo para notas multi-diagnóstico)
  const handleAddSelection = () => {
    if (!selectedDiagnosis || Object.keys(selected).length === 0) {
      return; // No hay nada que agregar
    }

    const newSelection = {
      id: Date.now(), // ID temporal para React keys
      diagnosis: selectedDiagnosis,
      selections: { ...selected },
      noteText: currentNoteText
    };

    setAddedSelections(prev => [...prev, newSelection]);
    
    // Limpiar SOLO la selección actual, no el odontograma completo
    setSelected({});
    setCurrentNoteText('');
  };

  // Eliminar una selección de la lista
  const handleRemoveSelection = (selectionId) => {
    setAddedSelections(prev => prev.filter(sel => sel.id !== selectionId));
  };

  // Combinar todas las selecciones para mostrar en el odontograma (solo para multi-diagnóstico)
  const getAllSelections = () => {
    if (!isMultiDiagnosis) {
      return selected; // Para notas tradicionales, usar selected directamente
    }

    const combined = {};
    
    // Agregar selecciones ya guardadas
    addedSelections.forEach(selection => {
      Object.entries(selection.selections).forEach(([toothLabel, surfaceData]) => {
        if (!combined[toothLabel]) {
          combined[toothLabel] = {};
        }
        Object.entries(surfaceData).forEach(([surface, color]) => {
          combined[toothLabel][surface] = color;
        });
      });
    });
    
    // Agregar selección actual (tiene prioridad si hay conflicto)
    Object.entries(selected).forEach(([toothLabel, surfaceData]) => {
      if (!combined[toothLabel]) {
        combined[toothLabel] = {};
      }
      Object.entries(surfaceData).forEach(([surface, color]) => {
        combined[toothLabel][surface] = color;
      });
    });
    
    return combined;
  };

  // Crear mapeo de diente+superficie a diagnóstico para el odontograma
  const getDiagnosisMap = () => {
    const toothSurfaceToDiagnosis = {};
    
    if (isMultiDiagnosis) {
      // Para notas multi-diagnóstico, mapear cada diente+superficie a su diagnóstico
      addedSelections.forEach(selection => {
        if (selection.diagnosis) {
          Object.entries(selection.selections).forEach(([toothLabel, surfaceData]) => {
            Object.entries(surfaceData).forEach(([surface, color]) => {
              const key = `${toothLabel}:${surface}`;
              toothSurfaceToDiagnosis[key] = selection.diagnosis;
            });
          });
        }
      });
      
      // Agregar selección actual si existe
      if (selectedDiagnosis) {
        Object.entries(selected).forEach(([toothLabel, surfaceData]) => {
          Object.entries(surfaceData).forEach(([surface, color]) => {
            const key = `${toothLabel}:${surface}`;
            toothSurfaceToDiagnosis[key] = selectedDiagnosis;
          });
        });
      }
    } else {
      // Para notas tradicionales, mapear cada diente+superficie al diagnóstico seleccionado
      if (selectedDiagnosis) {
        Object.entries(selected).forEach(([toothLabel, surfaceData]) => {
          Object.entries(surfaceData).forEach(([surface, color]) => {
            const key = `${toothLabel}:${surface}`;
            toothSurfaceToDiagnosis[key] = selectedDiagnosis;
          });
        });
      }
    }
    
    return toothSurfaceToDiagnosis;
  };

  const toggleSelection = (toothLabel, surface) => {
    if (!SURFACES.includes(surface)) return;
    
    // Para notas multi-diagnóstico, aplicar restricciones
    if (isMultiDiagnosis) {
      // Si no hay diagnóstico seleccionado, no permitir selección
      if (!selectedDiagnosis) {
        return;
      }
      
      // Verificar si esta superficie ya está en una selección agregada anteriormente
      const isInAddedSelections = addedSelections.some(selection => 
        selection.selections[toothLabel] && selection.selections[toothLabel][surface]
      );
      
      // Si está en selecciones agregadas, no permitir modificar
      if (isInAddedSelections) {
        return;
      }
    } else {
      // Para notas tradicionales, requerir diagnóstico
      if (!selectedDiagnosis) {
        return;
      }
    }
    
    setSelected(prev => {
      const toothData = prev[toothLabel] || {};
      const next = { ...prev };
      
      // Si el diagnóstico es para diente completo
      if (selectedDiagnosis.isCompleteTooth) {
        // Para notas multi-diagnóstico, verificar conflictos
        if (isMultiDiagnosis) {
          const hasAddedSurfaces = SURFACES.some(s => 
            addedSelections.some(selection => 
              selection.selections[toothLabel] && selection.selections[toothLabel][s]
            )
          );
          
          if (hasAddedSurfaces) {
            // No permitir marcar diente completo si ya hay superficies agregadas
            return prev;
          }
        }
        
        if (toothData[surface]) {
          // Si ya está marcado, desmarcar todo el diente
          delete next[toothLabel];
        } else {
          // Marcar todas las superficies del diente con el color del diagnóstico
          const allSurfaces = {};
          SURFACES.forEach(s => {
            allSurfaces[s] = selectedDiagnosis.color;
          });
          next[toothLabel] = allSurfaces;
        }
      } else {
        // Lógica para diagnósticos individuales
        if (toothData[surface]) {
          // Si ya está marcada, la desmarcamos
          const newToothData = { ...toothData };
          delete newToothData[surface];
          
          if (Object.keys(newToothData).length === 0) {
            delete next[toothLabel];
          } else {
            next[toothLabel] = newToothData;
          }
        } else {
          // Si no está marcada, la marcamos con el color del diagnóstico
          next[toothLabel] = {
            ...toothData,
            [surface]: selectedDiagnosis.color
          };
        }
      }
      
      return next;
    });
  };


  const normalizeSurface = (s) => {
    // Map UI keys to clinical terms for stored note metadata
    const map = {
      top: 'oclusal/incisal',
      right: 'distal',
      bottom: 'lingual/palatino',
      left: 'mesial',
      center: 'centro',
    };
    return map[s] || s;
  };

  const denormalizeSurface = (s) => {
    // Map clinical terms back to UI keys
    const map = {
      'oclusal/incisal': 'top',
      'distal': 'right',
      'lingual/palatino': 'bottom',
      'mesial': 'left',
      'centro': 'center',
    };
    return map[s] || s;
  };

  const handleSaveNotes = async () => {
    if (!auth.user?.access_token) {
      console.error('No authentication token available');
      return;
    }

    try {
      setLoading(true);

      let dentalNoteData;

      if (isMultiDiagnosis) {
        // Verificar que hay selecciones agregadas
        if (addedSelections.length === 0) {
          alert('No hay selecciones agregadas para guardar.');
          return;
        }

        // Crear estructura de datos con múltiples sub-notas
        const subNotes = addedSelections.map(selection => {
          const selections = [];
          Object.entries(selection.selections).forEach(([toothLabel, surfaceData]) => {
            Object.entries(surfaceData).forEach(([surface, color]) => {
              selections.push({
                tooth: toothLabel,
                surface: normalizeSurface(surface),
                color: color,
                colorId: color
              });
            });
          });

          return {
            diagnosisId: selection.diagnosis.id,
            selections: selections,
            text: selection.noteText || ''
          };
        });

        dentalNoteData = {
          subNotes: subNotes,
          mode,
          text: generalNotes || '', // Usar notas generales o vacío
        };
      } else {
        // Nota tradicional
        const selections = [];
        Object.entries(selected).forEach(([toothLabel, surfaceData]) => {
          Object.entries(surfaceData).forEach(([surface, color]) => {
            selections.push({
              tooth: toothLabel,
              surface: normalizeSurface(surface),
              color: color,
              colorId: color
            });
          });
        });

        dentalNoteData = {
          selections: selections,
          text: generalNotes || '',
          mode,
          diagnosisId: selectedDiagnosis?.id || null,
        };
      }

      // Actualizar en la API
      const response = await DentalNoteService.updateDentalNote(noteId, dentalNoteData, auth.user.access_token);
      
      if (response.success) {
        // Navegar de vuelta a la lista de notas dentales
        navigate(`/patients/${patientId}/dental-notes/legacy`);
      } else {
        console.error('Error updating dental note:', response.message);
      }
    } catch (error) {
      console.error('Error updating dental note:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!auth.user?.access_token) {
      console.error('No authentication token available');
      return;
    }

    if (!window.confirm('¿Estás seguro de que quieres eliminar esta nota dental? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await DentalNoteService.deleteDentalNote(noteId, false, auth.user.access_token);
      
      if (response.success) {
        // Navegar de vuelta a la lista de notas dentales
        navigate(`/patients/${patientId}/dental-notes/legacy`);
      } else {
        console.error('Error deleting dental note:', response.message);
      }
    } catch (error) {
      console.error('Error deleting dental note:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !originalNote) {
    return (
      <div className="new-dental-note-container">
        <div className="empty-state">
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Cargando nota dental...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="new-dental-note-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
        sectionTitle="Editar Odontograma"
        onBackClick={goBack}
      />

      <div className="odontogram-page-content">
        <div className="mode-switch">
          <label className={`mode-option ${mode === 'adult' ? 'active' : ''}`}>
            <input type="radio" name="mode" value="adult" checked={mode === 'adult'} onChange={() => setMode('adult')} />
            Adulto
          </label>
          <label className={`mode-option ${mode === 'pediatric' ? 'active' : ''}`}>
            <input type="radio" name="mode" value="pediatric" checked={mode === 'pediatric'} onChange={() => setMode('pediatric')} />
            Pediátrico
          </label>
        </div>

        {isMultiDiagnosis ? (
          // Vista para notas multi-diagnóstico
          <div className="selection-controls">
            <div className="control-section">
              <h4>Agregar nuevo diagnóstico:</h4>
              <DiagnosisDropdown
                selectedDiagnosis={selectedDiagnosis}
                onDiagnosisSelect={handleDiagnosisChange}
                placeholder="Buscar y seleccionar diagnóstico..."
              />
            </div>

            {selectedDiagnosis && (
              <div className="control-section">
                <h4>Detalles del diagnóstico:</h4>
                <div className="selected-diagnosis-info">
                  <div className="diagnosis-header">
                    <span className="diagnosis-name">
                      <i className="fas fa-stethoscope"></i>
                      {selectedDiagnosis.name}
                    </span>
                    <span 
                      className="diagnosis-color-dot" 
                      style={{ backgroundColor: selectedDiagnosis.color }}
                      title={`Color: ${selectedDiagnosis.color}`}
                    ></span>
                  </div>
                  <div className="diagnosis-type">
                    <i className={`fas ${selectedDiagnosis.isCompleteTooth ? 'fa-tooth' : 'fa-layer-group'}`}></i>
                    {selectedDiagnosis.isCompleteTooth ? 'Aplica a diente completo' : 'Aplica por cara individual'}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Vista para notas tradicionales
          <div className="selection-controls">
            <div className="control-section">
              <h4>Cambiar diagnóstico:</h4>
              <DiagnosisDropdown
                selectedDiagnosis={selectedDiagnosis}
                onDiagnosisSelect={setSelectedDiagnosis}
                placeholder="Buscar y seleccionar diagnóstico..."
              />
            </div>

            {selectedDiagnosis && (
              <div className="control-section">
                <h4>Detalles del diagnóstico:</h4>
                <div className="selected-diagnosis-info">
                  <div className="diagnosis-header">
                    <span className="diagnosis-name">
                      <i className="fas fa-stethoscope"></i>
                      {selectedDiagnosis.name}
                    </span>
                    <span 
                      className="diagnosis-color-dot" 
                      style={{ backgroundColor: selectedDiagnosis.color }}
                      title={`Color: ${selectedDiagnosis.color}`}
                    ></span>
                  </div>
                  <div className="diagnosis-type">
                    <i className={`fas ${selectedDiagnosis.isCompleteTooth ? 'fa-tooth' : 'fa-layer-group'}`}></i>
                    {selectedDiagnosis.isCompleteTooth ? 'Aplica a diente completo' : 'Aplica por cara individual'}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="odontogram-panel">
          <Odontogram 
            mode={mode} 
            selected={getAllSelections()} 
            onToggle={toggleSelection}
            diagnosisMap={getDiagnosisMap()}
          />
        </div>

        <div className="note-and-selections">
          {isMultiDiagnosis ? (
            // Vista para notas multi-diagnóstico
            <>
              {/* Sección de selección actual */}
              <div className="current-selection">
                <h3>Selección Actual</h3>
                <div className="current-note">
                  <label>
                    <i className="fas fa-comment-medical"></i>
                    Nota específica para este diagnóstico:
                  </label>
                  <textarea
                    placeholder="Descripción específica para este diagnóstico (opcional)..."
                    value={currentNoteText}
                    onChange={(e) => handleCurrentNoteTextChange(e.target.value)}
                  />
                </div>
                
                <div className="current-selection-panel">
                  {Object.keys(selected).length === 0 ? (
                    <p className="muted">
                      {selectedDiagnosis 
                        ? `Haz clic en ${selectedDiagnosis.isCompleteTooth ? 'cualquier superficie del diente' : 'las superficies específicas'} para marcarlas con el diagnóstico "${selectedDiagnosis.name}".`
                        : 'Primero selecciona un diagnóstico para comenzar a marcar dientes.'
                      }
                    </p>
                  ) : (
                    <>
                      <div className="selection-grid">
                        {Object.entries(selected).map(([toothLabel, surfaceData]) => (
                          <div key={toothLabel} className="selection-group">
                            <div className="selection-group-header">
                              <span className="badge tooth">{toothLabel}</span>
                              <span className="group-count">{Object.keys(surfaceData).length}</span>
                            </div>
                            <div className="selection-chips">
                              {Object.entries(surfaceData).map(([surface, color]) => {
                                const surfaceName = getSurfaceName(toothLabel, surface);
                                return (
                                  <button
                                    key={`${toothLabel}:${surface}`}
                                    className={`chip surface ${surface}`}
                                    style={{ 
                                      backgroundColor: `${color}40`,
                                      borderColor: color
                                    }}
                                    onClick={() => toggleSelection(toothLabel, surface)}
                                    title={`${surfaceName} (Quitar selección)`}
                                  >
                                    {surfaceName}
                                    <span className="chip-close">×</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="add-selection-action">
                        <Button 
                          variant="primary" 
                          icon="fas fa-plus"
                          onClick={handleAddSelection}
                          disabled={!selectedDiagnosis || Object.keys(selected).length === 0}
                        >
                          Agregar Selección
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Lista de selecciones agregadas */}
              <div className="added-selections">
                <h3>Selecciones Guardadas ({addedSelections.length})</h3>
                {addedSelections.length === 0 ? (
                  <p className="muted">Esta nota no tiene selecciones guardadas.</p>
                ) : (
                  <div className="added-selections-list">
                    {addedSelections.map((selection) => (
                      <div key={selection.id} className="added-selection-item">
                        <div className="added-selection-header">
                          <div className="diagnosis-info">
                            <span className="diagnosis-name">
                              <i className="fas fa-stethoscope"></i>
                              {selection.diagnosis.name}
                            </span>
                          </div>
                          <button 
                            className="btn btn-danger btn-sm"
                            onClick={() => handleRemoveSelection(selection.id)}
                            title="Eliminar selección"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                        
                        <div className="selection-content">
                          <div className="teeth-selections">
                            {Object.entries(selection.selections).map(([toothLabel, surfaceData]) => (
                              <div key={toothLabel} className="tooth-selection">
                                <span className="tooth-label">{toothLabel}:</span>
                                <div className="surfaces">
                                  {Object.keys(surfaceData).map(surface => {
                                    const surfaceName = getSurfaceName(toothLabel, surface);
                                    return (
                                      <span key={surface} className="surface-chip">
                                        {surfaceName}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {selection.noteText && (
                            <div className="selection-note">
                              <strong>Nota:</strong> {selection.noteText}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            // Vista para notas tradicionales
            <>
              <div className="global-note">
                <h3>Nota</h3>
                <textarea
                  placeholder="Descripción de la nota para las selecciones"
                  value={currentNoteText}
                  onChange={(e) => handleCurrentNoteTextChange(e.target.value)}
                />
              </div>
              <div className="selection-panel">
                <h3>Selecciones</h3>
                {Object.keys(selected).length === 0 ? (
                  <p className="muted">
                    {selectedDiagnosis 
                      ? `Haz clic en ${selectedDiagnosis.isCompleteTooth ? 'cualquier superficie del diente' : 'las superficies específicas'} para marcarlas con el diagnóstico "${selectedDiagnosis.name}".`
                      : 'Primero selecciona un diagnóstico para comenzar a marcar dientes.'
                    }
                  </p>
                ) : (
                  <div className="selection-grid">
                    {Object.entries(selected).map(([toothLabel, surfaceData]) => (
                      <div key={toothLabel} className="selection-group">
                        <div className="selection-group-header">
                          <span className="badge tooth">{toothLabel}</span>
                          <span className="group-count">{Object.keys(surfaceData).length}</span>
                        </div>
                        <div className="selection-chips">
                          {Object.entries(surfaceData).map(([surface, color]) => {
                            const surfaceName = getSurfaceName(toothLabel, surface);
                            return (
                              <button
                                key={`${toothLabel}:${surface}`}
                                className={`chip surface ${surface}`}
                                style={{ 
                                  backgroundColor: `${color}40`, // Agregar transparencia al color
                                  borderColor: color
                                }}
                                onClick={() => toggleSelection(toothLabel, surface)}
                                title={`${surfaceName} (Quitar selección)`}
                              >
                                {surfaceName}
                                <span className="chip-close">×</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Sección de notas generales */}
        <div className="general-notes-section">
          <h3>
            <i className="fas fa-file-medical-alt"></i>
            Notas Generales de la Consulta
          </h3>
          <p className="section-description">
            Estas notas se aplicarán a toda la nota dental y son independientes de los diagnósticos específicos.
          </p>
          <div className="general-notes-input">
            <textarea
              placeholder="Agregar observaciones generales de la consulta, hallazgos importantes, recomendaciones, etc..."
              value={generalNotes}
              onChange={(e) => setGeneralNotes(e.target.value)}
              rows="4"
            />
            <div className="character-counter">
              {generalNotes.length} caracteres
            </div>
          </div>
        </div>

        <div className="page-actions">
          <div className="page-actions-left">
            <Button 
              variant="danger" 
              icon="fas fa-trash" 
              onClick={handleDelete}
              disabled={loading}
            >
              Eliminar
            </Button>
          </div>
          <div className="page-actions-right">
            <Button variant="outline" onClick={goBack}>Cancelar</Button>
            <Button 
              variant="primary" 
              icon={loading ? "fas fa-spinner fa-spin" : "fas fa-save"} 
              onClick={handleSaveNotes}
              disabled={loading || (isMultiDiagnosis && addedSelections.length === 0)}
            >
              {loading 
                ? 'Guardando...' 
                : isMultiDiagnosis 
                  ? `Guardar Nota (${addedSelections.length} selecciones)`
                  : 'Guardar cambios'
              }
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditDentalNote;
